/**
 * Warmth Score v1 — pure ranking helpers for the Lead Ranking dial queue.
 * Caller-type-agnostic (one queue for human or AI callers).
 * Scores ONLY real prospect-row fields from ProspectsService.getProspects():
 *   last_dispo, available_date, employee_size, plan_assets.
 *
 * Dispo codes are the system's canonical vocabulary (same as the Dispos/Callers reports).
 * origin/develop exposes no shared dispo-constants module to import (dispo-reports keeps them
 * inline; campaign-effort's live on a separate branch), so the ranking TIERS are defined here
 * for this feature — ranking-specific groupings, not a redefinition of a shared dictionary.
 *
 * Recency/attempt decay is intentionally NOT modelled — last_dispo_date & attempt_count are not
 * on the prospect row yet (pending backend). See the in-app note.
 */
export type WarmthTier = 'hot' | 'warm' | 'cold' | 'excluded';
export type CallbackState = 'overdue' | 'today' | 'future' | 'none';

// ── Ranking tiers (v1 spec) — canonical dispo codes ──
export const TIER1_HOT = ['HOT', 'SINFO'];
export const TIER2_WARM = ['FYI'];
export const TIER3_COLD = ['NOANS', 'NOMSG', 'MSG', 'GKINFO', 'TREE']; // (+ empty last_dispo)
export const EXCLUDE_WON = ['FACE', 'PHONE', 'ZOOM', 'FIX', 'LEADF', 'LEADP'];
export const EXCLUDE_DEAD = ['FAX', 'BUSY', 'DISCO', 'NOTINT', 'WRONG', 'DONOTCALL', 'NOMARKET'];

// Inferred meanings for the reason line (confirm w/ ops — no label dictionary in-app).
export const DISPO_MEANING: { [code: string]: string } = {
	HOT: 'Hot — strong interest',
	SINFO: 'Sent info — engaged',
	FYI: 'FYI — soft interest',
	NOANS: 'No answer',
	NOMSG: 'No message left',
	MSG: 'Message left',
	GKINFO: 'Gatekeeper info',
	TREE: 'Phone tree',
};

export interface RankedProspect {
	prospect: any;
	tier: WarmthTier;
	tierRank: number;
	tierLabel: string;
	callbackState: CallbackState;
	callbackTime: number; // ms for sort; 0 when no real callback
	employeesFit: number | null; // sanitized for tie-break/display (null = unknown)
	planAssetsFit: number | null;
	reason: string;
}

export interface RankResult {
	ranked: RankedProspect[];
	excludedCount: number;
	counts: { hot: number; warm: number; cold: number };
}

function norm(v: any): string {
	return (v == null ? '' : String(v)).toUpperCase().trim();
}
function num(v: any): number {
	const n = parseFloat(v);
	return isNaN(n) ? 0 : n;
}
function parseDate(s: any): Date | null {
	if (!s) return null;
	const t = Date.parse(String(s));
	return isNaN(t) ? null : new Date(t);
}

// ── Guards ───────────────────────────────────────────────────────────────────
// Backend defaults available_date to a stale sentinel (reported ~2022-01-01) when there's no
// real callback. Any date on/before this cutoff = NO callback (never "overdue"). Real callbacks
// are current/future, so genuine recent-overdue callbacks still register.
export const CALLBACK_SENTINEL_ON_OR_BEFORE = new Date(2022, 0, 1); // 2022-01-01 (local)
// Implausible numeric guards for the fit tie-break.
export const INT32_MAX = 2147483647; // common DB "no value" sentinel (MAX_INT)
export const EMPLOYEE_MAX_PLAUSIBLE = 2000000;

function startOfDay(d: Date): number {
	return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}
/** Numeric fit value for tie-break; returns null (= unknown) for absent/absurd/sentinel values. */
function fitNum(v: any, softMax?: number): number | null {
	const n = num(v);
	if (n <= 0) return null;
	if (n === INT32_MAX) return null; // MAX_INT sentinel → unknown
	if (softMax != null && n > softMax) return null; // implausibly large → unknown
	return n;
}

export function tierOf(lastDispo: any): { tier: WarmthTier; rank: number; label: string } {
	const d = norm(lastDispo);
	if (EXCLUDE_WON.indexOf(d) >= 0 || EXCLUDE_DEAD.indexOf(d) >= 0) return { tier: 'excluded', rank: 99, label: '' };
	if (TIER1_HOT.indexOf(d) >= 0) return { tier: 'hot', rank: 1, label: 'Hot' };
	if (TIER2_WARM.indexOf(d) >= 0) return { tier: 'warm', rank: 2, label: 'Warm' };
	// Tier 3: explicit cold codes OR no last_dispo OR any unknown code (retryable — never silently dropped)
	return { tier: 'cold', rank: 3, label: 'Cold' };
}

export function callbackStateOf(availableDate: any, today: Date): CallbackState {
	const d = parseDate(availableDate);
	if (!d) return 'none';
	// Stale backend sentinel (~2022-01-01) → treat as NO callback, never "overdue".
	if (startOfDay(d) <= startOfDay(CALLBACK_SENTINEL_ON_OR_BEFORE)) return 'none';
	const startToday = startOfDay(today);
	const startD = startOfDay(d);
	if (startD < startToday) return 'overdue';
	if (startD === startToday) return 'today';
	return 'future';
}

function callbackBucket(cs: CallbackState): number {
	return cs === 'overdue' || cs === 'today' ? 0 : cs === 'none' ? 1 : 2;
}

/** True when there is NO real, current callback (absent, or a stale backend sentinel ≤ 2022-01-01). */
function isSentinelOrEmpty(availableDate: any): boolean {
	const d = parseDate(availableDate);
	if (!d) return true;
	return startOfDay(d) <= startOfDay(CALLBACK_SENTINEL_ON_OR_BEFORE);
}

function reasonOf(lastDispo: any, cs: CallbackState, availableDate: any): string {
	const d = norm(lastDispo);
	const base = DISPO_MEANING[d] || (d ? `Dispo: ${d}` : 'Not yet contacted');
	// Callback clause appears ONLY for a real, non-sentinel callback. cs already excludes the
	// sentinel/cold/fresh cases, but we re-check the DATE here so a stale "2022-01-01" can never
	// be appended regardless of how cs was derived — a fresh lead reads just "Not yet contacted".
	if (cs === 'none' || isSentinelOrEmpty(availableDate)) return base;
	const dateStr = String(availableDate).split(' ')[0];
	if (cs === 'overdue') return `${base} · callback overdue (${dateStr})`;
	if (cs === 'today') return `${base} · callback due today`;
	if (cs === 'future') return `${base} · callback ${dateStr}`;
	return base;
}

export function rankQueue(prospects: any[], today: Date): RankResult {
	const rows = Array.isArray(prospects) ? prospects : [];
	const ranked: RankedProspect[] = [];
	let excludedCount = 0;
	const counts = { hot: 0, warm: 0, cold: 0 };

	for (const p of rows) {
		const t = tierOf(p?.last_dispo);
		if (t.tier === 'excluded') {
			excludedCount++;
			continue;
		}
		// Callback due/overdue is meaningful ONLY for warm-tier (Hot/Warm) leads with a REAL date.
		// Cold / never-contacted → no callback semantics ("Not yet contacted", never "overdue").
		const isWarm = t.tier === 'hot' || t.tier === 'warm';
		const cs: CallbackState = isWarm ? callbackStateOf(p?.available_date, today) : 'none';
		const callbackTime = cs === 'none' ? 0 : (parseDate(p?.available_date)?.getTime() ?? 0);
		ranked.push({
			prospect: p,
			tier: t.tier,
			tierRank: t.rank,
			tierLabel: t.label,
			callbackState: cs,
			callbackTime,
			employeesFit: fitNum(p?.employee_size, EMPLOYEE_MAX_PLAUSIBLE),
			planAssetsFit: fitNum(p?.plan_assets),
			reason: reasonOf(p?.last_dispo, cs, p?.available_date),
		});
		counts[t.tier as 'hot' | 'warm' | 'cold']++;
	}

	ranked.sort((a, b) => {
		if (a.tierRank !== b.tierRank) return a.tierRank - b.tierRank; // tier
		const ba = callbackBucket(a.callbackState);
		const bb = callbackBucket(b.callbackState);
		if (ba !== bb) return ba - bb; // due → none → future
		if (a.callbackTime !== b.callbackTime) return a.callbackTime - b.callbackTime; // earliest first
		const es = (b.employeesFit ?? 0) - (a.employeesFit ?? 0);
		if (es !== 0) return es; // employees desc (unknown sorts last)
		return (b.planAssetsFit ?? 0) - (a.planAssetsFit ?? 0); // plan_assets desc (unknown last)
	});

	return { ranked, excludedCount, counts };
}
